/**
 * NarrativeBlock — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Visual spec: Sally (Discourse #3543/4) — Section 4
 *
 * Upgraded to glassmorphism card with emphasis-based styling:
 * - Glassmorphism backdrop-blur background
 * - Emphasis levels: normal, high (purple accent), critical (red accent)
 * - Dark theme optimized text colors
 *
 * Task: [SDUI-M.P3.2]
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { cn } from "../../lib/utils";
import type { NarrativeBlockProps } from "../schema";

/* ------------------------------------------------------------------ */
/*  Emphasis style map                                                 */
/* ------------------------------------------------------------------ */

const emphasisStyles: Record<
  "normal" | "high" | "critical",
  { container: string; text: string }
> = {
  normal: {
    container:
      "backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl",
    text: "text-[#E5E7EB] font-normal",
  },
  high: {
    container:
      "backdrop-blur-sm bg-white/5 border border-white/10 border-l-[3px] border-l-[#8b5cf6] rounded-xl",
    text: "text-[#F3F4F6] font-medium",
  },
  critical: {
    container:
      "backdrop-blur-sm bg-red-500/5 border border-red-500/20 border-l-[3px] border-l-red-500 rounded-xl",
    text: "text-[#F3F4F6] font-semibold",
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function NarrativeBlock({
  text,
  emphasis = "normal",
}: NarrativeBlockProps) {
  const style = emphasisStyles[emphasis];

  return (
    <div className={cn("px-5 py-4", style.container)}>
      <p
        className={cn(
          "text-sm leading-relaxed",
          style.text
        )}
      >
        {text}
      </p>
    </div>
  );
}
